import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
} from '@mui/material'
import {
  Api as ApiIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Key as KeyIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'

const ApiSettings = ({ settings = {}, onChange, isLoading = false }) => {
  const [localSettings, setLocalSettings] = useState({
    // API Configuration
    apiEnabled: true,
    apiVersion: 'v1',
    apiBaseUrl: 'https://api.virtualsms.com',
    apiTimeout: 30,
    
    // Rate limiting
    enableRateLimit: true,
    rateLimit: 1000,
    rateLimitWindow: 3600,
    burstLimit: 100,
    
    // Authentication
    requireApiKey: true,
    apiKeyLength: 32,
    keyRotationDays: 90,
    allowMultipleKeys: true,
    
    // CORS settings
    enableCors: true,
    corsOrigins: '',
    allowCredentials: false,
    corsMaxAge: 86400,
    
    // Webhook settings
    enableWebhooks: true,
    webhookTimeout: 30,
    webhookRetries: 3,
    webhookSecretLength: 32,
    
    // Monitoring
    enableApiLogs: true,
    logLevel: 'info',
    retentionDays: 30,
    enableMetrics: true,
    
    // Cache settings
    enableCache: true,
    cacheProvider: 'redis',
    cacheTtl: 300,
    
    ...settings
  })

  const [apiKeys, setApiKeys] = useState([
    {
      id: '1',
      name: 'Production API',
      key: 'vms_live_1234567890abcdef1234567890abcdef',
      permissions: ['read', 'write'],
      lastUsed: '2024-01-15T10:30:00Z',
      created: '2024-01-01T00:00:00Z',
      active: true
    },
    {
      id: '2',
      name: 'Mobile App',
      key: 'vms_live_abcdef1234567890abcdef1234567890',
      permissions: ['read'],
      lastUsed: '2024-01-15T12:15:00Z',
      created: '2024-01-10T00:00:00Z',
      active: true
    }
  ])

  const [showKeys, setShowKeys] = useState({})
  const [keyDialog, setKeyDialog] = useState({ open: false, key: null })

  const handleSettingChange = (key, value) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: value
    }))
    onChange?.()
  }

  const toggleKeyVisibility = (keyId) => {
    setShowKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }))
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
    // toast.success('API key kopyalandı')
  }

  const generateApiKey = () => {
    const chars = 'abcdef0123456789'
    let result = 'vms_live_'
    for (let i = 0; i < localSettings.apiKeyLength; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  const handleCreateKey = (keyData) => {
    const newKey = {
      id: Date.now().toString(),
      name: keyData.name,
      key: generateApiKey(),
      permissions: keyData.permissions,
      lastUsed: null,
      created: new Date().toISOString(),
      active: true
    }
    
    setApiKeys(prev => [...prev, newKey])
    setKeyDialog({ open: false, key: null })
  }

  const handleRevokeKey = (keyId) => {
    setApiKeys(prev => prev.map(key => 
      key.id === keyId ? { ...key, active: false } : key
    ))
  }

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString('tr-TR') : 'Hiç kullanılmadı'
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          API Ayarları
        </Typography>
        <Typography variant="body1" color="text.secondary">
          API konfigürasyonu ve entegrasyon ayarları
        </Typography>
      </Box>

      {/* API Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ApiIcon />
            Genel API Ayarları
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="API Erişimi"
                secondary="REST API endpoint'lerini etkinleştir/devre dışı bırak"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.apiEnabled}
                  onChange={(e) => handleSettingChange('apiEnabled', e.target.checked)}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary="API Key Zorunluluğu"
                secondary="Tüm API istekleri için API key kontrolü"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.requireApiKey}
                  onChange={(e) => handleSettingChange('requireApiKey', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>API Versiyonu</InputLabel>
                <Select
                  value={localSettings.apiVersion}
                  label="API Versiyonu"
                  onChange={(e) => handleSettingChange('apiVersion', e.target.value)}
                >
                  <MenuItem value="v1">v1 (Mevcut)</MenuItem>
                  <MenuItem value="v2">v2 (Beta)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Timeout (saniye)"
                type="number"
                value={localSettings.apiTimeout}
                onChange={(e) => handleSettingChange('apiTimeout', parseInt(e.target.value) || 30)}
                helperText="API istekleri için zaman aşımı süresi"
                inputProps={{ min: 5, max: 300 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Base URL"
                value={localSettings.apiBaseUrl}
                onChange={(e) => handleSettingChange('apiBaseUrl', e.target.value)}
                helperText="API endpoint'lerinin base URL adresi"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Rate Limiting */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon />
            Rate Limiting
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableRateLimit}
                onChange={(e) => handleSettingChange('enableRateLimit', e.target.checked)}
              />
            }
            label="Rate limiting etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableRateLimit && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="İstek Limiti"
                  type="number"
                  value={localSettings.rateLimit}
                  onChange={(e) => handleSettingChange('rateLimit', parseInt(e.target.value) || 1000)}
                  helperText="Zaman aralığında izin verilen maksimum istek sayısı"
                  inputProps={{ min: 10, max: 10000 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Zaman Aralığı (saniye)"
                  type="number"
                  value={localSettings.rateLimitWindow}
                  onChange={(e) => handleSettingChange('rateLimitWindow', parseInt(e.target.value) || 3600)}
                  helperText="Rate limit hesaplama zaman aralığı"
                  inputProps={{ min: 60, max: 86400 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Burst Limiti"
                  type="number"
                  value={localSettings.burstLimit}
                  onChange={(e) => handleSettingChange('burstLimit', parseInt(e.target.value) || 100)}
                  helperText="Ani istek patlamaları için limit"
                  inputProps={{ min: 5, max: 1000 }}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* API Keys Management */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <KeyIcon />
              API Anahtarları
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setKeyDialog({ open: true, key: null })}
              disabled={!localSettings.allowMultipleKeys && apiKeys.filter(k => k.active).length > 0}
            >
              Yeni Anahtar
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Ad</TableCell>
                  <TableCell>API Key</TableCell>
                  <TableCell>İzinler</TableCell>
                  <TableCell>Son Kullanım</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((apiKey) => (
                  <TableRow key={apiKey.id}>
                    <TableCell>{apiKey.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {showKeys[apiKey.id] ? apiKey.key : '••••••••••••••••••••••••••••••••'}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => toggleKeyVisibility(apiKey.id)}
                        >
                          {showKeys[apiKey.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(apiKey.key)}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {apiKey.permissions.map((permission) => (
                          <Chip 
                            key={permission} 
                            label={permission} 
                            size="small" 
                            color={permission === 'write' ? 'warning' : 'info'}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(apiKey.lastUsed)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={apiKey.active ? 'Aktif' : 'İptal Edildi'} 
                        color={apiKey.active ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {apiKey.active && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => setKeyDialog({ open: true, key: apiKey })}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeKey(apiKey.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Key Uzunluğu"
                type="number"
                value={localSettings.apiKeyLength}
                onChange={(e) => handleSettingChange('apiKeyLength', parseInt(e.target.value) || 32)}
                helperText="Yeni oluşturulan API key'lerin karakter uzunluğu"
                inputProps={{ min: 16, max: 64 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Key Rotasyon Süresi (gün)"
                type="number"
                value={localSettings.keyRotationDays}
                onChange={(e) => handleSettingChange('keyRotationDays', parseInt(e.target.value) || 90)}
                helperText="API key'lerin otomatik yenileme süresi"
                inputProps={{ min: 30, max: 365 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.allowMultipleKeys}
                    onChange={(e) => handleSettingChange('allowMultipleKeys', e.target.checked)}
                  />
                }
                label="Birden fazla API key'e izin ver"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* CORS Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            CORS Ayarları
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableCors}
                onChange={(e) => handleSettingChange('enableCors', e.target.checked)}
              />
            }
            label="CORS desteğini etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableCors && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="İzinli Origin'ler"
                  value={localSettings.corsOrigins}
                  onChange={(e) => handleSettingChange('corsOrigins', e.target.value)}
                  helperText="İzinli domain'ler (virgül ile ayırın, * tüm domain'ler için)"
                  placeholder="https://example.com, https://app.example.com"
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.allowCredentials}
                      onChange={(e) => handleSettingChange('allowCredentials', e.target.checked)}
                    />
                  }
                  label="Credential'lara izin ver"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Max Age (saniye)"
                  type="number"
                  value={localSettings.corsMaxAge}
                  onChange={(e) => handleSettingChange('corsMaxAge', parseInt(e.target.value) || 86400)}
                  helperText="Preflight isteği cache süresi"
                  inputProps={{ min: 0, max: 86400 }}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Monitoring & Logging */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CodeIcon />
            İzleme ve Loglama
          </Typography>
          
          <List>
            <ListItem>
              <ListItemText
                primary="API Logları"
                secondary="Tüm API isteklerini kaydet"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.enableApiLogs}
                  onChange={(e) => handleSettingChange('enableApiLogs', e.target.checked)}
                  color="info"
                />
              </ListItemSecondaryAction>
            </ListItem>
            
            <Divider />
            
            <ListItem>
              <ListItemText
                primary="Metrikleri Etkinleştir"
                secondary="API kullanım istatistikleri ve performans metrikleri"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={localSettings.enableMetrics}
                  onChange={(e) => handleSettingChange('enableMetrics', e.target.checked)}
                  color="success"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>

          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Log Seviyesi</InputLabel>
                <Select
                  value={localSettings.logLevel}
                  label="Log Seviyesi"
                  onChange={(e) => handleSettingChange('logLevel', e.target.value)}
                >
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="warn">Warning</MenuItem>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="debug">Debug</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Log Saklama Süresi (gün)"
                type="number"
                value={localSettings.retentionDays}
                onChange={(e) => handleSettingChange('retentionDays', parseInt(e.target.value) || 30)}
                helperText="Log kayıtlarının silinmeden önce saklanma süresi"
                inputProps={{ min: 7, max: 365 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cache Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Cache Ayarları
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableCache}
                onChange={(e) => handleSettingChange('enableCache', e.target.checked)}
              />
            }
            label="API cache'i etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableCache && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Cache Sağlayıcı</InputLabel>
                  <Select
                    value={localSettings.cacheProvider}
                    label="Cache Sağlayıcı"
                    onChange={(e) => handleSettingChange('cacheProvider', e.target.value)}
                  >
                    <MenuItem value="redis">Redis</MenuItem>
                    <MenuItem value="memcached">Memcached</MenuItem>
                    <MenuItem value="memory">In-Memory</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="TTL (saniye)"
                  type="number"
                  value={localSettings.cacheTtl}
                  onChange={(e) => handleSettingChange('cacheTtl', parseInt(e.target.value) || 300)}
                  helperText="Cache'te veri tutma süresi"
                  inputProps={{ min: 60, max: 3600 }}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Webhook Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Webhook Ayarları
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.enableWebhooks}
                onChange={(e) => handleSettingChange('enableWebhooks', e.target.checked)}
              />
            }
            label="Webhook desteğini etkinleştir"
            sx={{ mb: 3 }}
          />

          {localSettings.enableWebhooks && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Webhook Timeout (saniye)"
                  type="number"
                  value={localSettings.webhookTimeout}
                  onChange={(e) => handleSettingChange('webhookTimeout', parseInt(e.target.value) || 30)}
                  helperText="Webhook HTTP isteği zaman aşımı"
                  inputProps={{ min: 5, max: 300 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Tekrar Denemeler"
                  type="number"
                  value={localSettings.webhookRetries}
                  onChange={(e) => handleSettingChange('webhookRetries', parseInt(e.target.value) || 3)}
                  helperText="Başarısız webhook için tekrar deneme sayısı"
                  inputProps={{ min: 0, max: 10 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Secret Uzunluğu"
                  type="number"
                  value={localSettings.webhookSecretLength}
                  onChange={(e) => handleSettingChange('webhookSecretLength', parseInt(e.target.value) || 32)}
                  helperText="Webhook imza anahtarı uzunluğu"
                  inputProps={{ min: 16, max: 64 }}
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Security Warnings */}
      <Box sx={{ mb: 3 }}>
        {!localSettings.requireApiKey && localSettings.apiEnabled && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Kritik Güvenlik Uyarısı:</strong> API erişimi etkin ancak API key zorunluluğu devre dışı. 
              Bu durum güvenlik riski oluşturur.
            </Typography>
          </Alert>
        )}

        {localSettings.enableCors && localSettings.corsOrigins.includes('*') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Güvenlik Uyarısı:</strong> CORS ayarlarında wildcard (*) kullanılıyor. 
              Güvenlik için spesifik domain'ler belirtilmesi önerilir.
            </Typography>
          </Alert>
        )}

        {!localSettings.enableRateLimit && localSettings.apiEnabled && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Performans Uyarısı:</strong> Rate limiting devre dışı. 
              DDoS saldırılarına karşı korunmak için rate limiting etkinleştirilmelidir.
            </Typography>
          </Alert>
        )}
      </Box>

      {/* API Status */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            API Durumu
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.apiEnabled ? "Aktif" : "Pasif"}
                  color={localSettings.apiEnabled ? "success" : "error"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  API Durumu
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.enableRateLimit ? "Etkin" : "Pasif"}
                  color={localSettings.enableRateLimit ? "success" : "warning"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  Rate Limiting
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={`${apiKeys.filter(k => k.active).length} Aktif`}
                  color="info"
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  API Keys
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Chip 
                  label={localSettings.enableCache ? "Etkin" : "Pasif"}
                  color={localSettings.enableCache ? "success" : "default"}
                  sx={{ mb: 1 }}
                />
                <Typography variant="caption" display="block">
                  Cache
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Key Dialog */}
      <Dialog
        open={keyDialog.open}
        onClose={() => setKeyDialog({ open: false, key: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {keyDialog.key ? 'API Key Düzenle' : 'Yeni API Key'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Key Adı"
                defaultValue={keyDialog.key?.name || ''}
                helperText="API key için açıklayıcı bir ad"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>İzinler</InputLabel>
                <Select
                  multiple
                  value={keyDialog.key?.permissions || ['read']}
                  label="İzinler"
                >
                  <MenuItem value="read">Read (Okuma)</MenuItem>
                  <MenuItem value="write">Write (Yazma)</MenuItem>
                  <MenuItem value="delete">Delete (Silme)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDialog({ open: false, key: null })}>
            İptal
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleCreateKey({ 
              name: 'New API Key', 
              permissions: ['read'] 
            })}
          >
            {keyDialog.key ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ApiSettings